import React, { useState, useEffect, FC, useContext } from 'react';
import {
  alpha,
  Box,
  CircularProgress,
  createTheme,
  hexToRgb,
  useTheme,
  ThemeProvider,
  CssBaseline,
  LinearProgress,
} from '@mui/material';
import SurveyFooter from '../../components/SurveyFooter/SurveyFooter';
import SurveyService from '../../services/SurveyService';
import { ISurvey } from '../../shared/interfaces/ISurvey';
import SurveyHeader from '../../components/SurveyHeader/SurveyHeader';
import Section from '../../components/Main/Section/Section';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import StandardSurveyContent from '../../components/StandardSurveyContent/StandardSurveyContent';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';
import i18n from 'i18next';

const StandardSurveyPage: FC = () => {
  const { t } = useTranslation();
  const { setCurrentView } = useContext(CurrentViewContext);
  const theme = useTheme();

  const [standardSurveyPage, setStandardSurveyPage] = useState<ISurvey | null>(null);
  const localTranslation = useLocalTranslation(
    standardSurveyPage ? standardSurveyPage : { lang: 'de' }
  );

  const [currentId, setCurrentId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const controller = new AbortController();
    const splitUrl = window.location.href.split('/');
    const extractValue = splitUrl[splitUrl.length - 1].toString();

    const getSurveyData = async () => {
      try {
        const response = await SurveyService.getSurveyPublic(
          extractValue,
          'standard',
          setLoading,
          controller.signal
        );
        if (response) {
          setCurrentId(response.id);
          setStandardSurveyPage(response);
          if (response.language) {
            i18n.changeLanguage(response.language);
          }
        }
      } catch (error) {
        console.error('Fehler beim Abrufen der Umfrage:', error);
      }
    };

    getSurveyData();

    return () => {
      controller.abort();
    };
  }, [currentId]);

  useEffect(() => {
    setCurrentView(t('survey'));
    document.title = standardSurveyPage?.label
      ? standardSurveyPage?.label
      : `Reputativ ${t('survey')}`;
  }, [setCurrentView, t, standardSurveyPage]);

  const getFontFormatByFileExtension = (fontFormat: FontFileExtensions) => {
    return {
      woff2: 'woff2',
      woff: 'woff',
      ttf: 'truetype',
    }[fontFormat];
  };

  if (standardSurveyPage) {
    const customTheme = createTheme({
      palette: {
        primary: {
          main: standardSurveyPage.primaryColor
            ? standardSurveyPage.primaryColor
            : theme.palette.primary.main,
        },
        divider: standardSurveyPage.secondaryColor
          ? alpha(hexToRgb(standardSurveyPage.secondaryColor), 0.2)
          : theme.palette.divider,
      },
      typography: {
        fontFamily:
          standardSurveyPage.customFont && standardSurveyPage.customFontFormat
            ? `"${standardSurveyPage.id}"`
            : '"Arial", "Helvetica", sans-serif',
      },
      components: {
        MuiCssBaseline: {
          styleOverrides:
            standardSurveyPage.customFont && standardSurveyPage.customFontFormat
              ? `
           @font-face {
            font-family: "${standardSurveyPage.id}";
            src: url(${standardSurveyPage.customFont}) format("${getFontFormatByFileExtension(
                  standardSurveyPage.customFontFormat as FontFileExtensions
                )}");
           }
        `
              : undefined,
        },
      },
    });

    return (
      <ThemeProvider theme={customTheme}>
        <CssBaseline />
        {loading && <LinearProgress />}
        <LocalTranslationContext.Provider value={localTranslation}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100%',
            }}
          >
            <SurveyHeader logo={standardSurveyPage.companyLogo} />
            <StandardSurveyContent {...standardSurveyPage} />
            <SurveyFooter
              company={standardSurveyPage.companyName}
              privacyPolicyLink={standardSurveyPage.privacyPolicyLink}
              imprintLink={standardSurveyPage.imprintLink}
            />
          </Box>
        </LocalTranslationContext.Provider>
      </ThemeProvider>
    );
  } else {
    return (
      <Section>
        <Box
          sx={{
            display: 'flex',
            alignSelf: 'center',
            alignItems: 'center',
            flex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      </Section>
    );
  }
};

export default StandardSurveyPage;
